/**
 * Edición en línea de la serie mensual: el reemplazo de editar la celda del
 * sheet, con la diferencia de que queda quién lo cargó y cuándo.
 *
 * Todo lo de este archivo es puro. La parte que importa es `aplicarEdiciones`:
 * recalcula net new MRR y growth MoM **con las mismas funciones del contrato que
 * usa el servidor** (`netNewMrr`, `growthMoMPct`). Con una segunda
 * implementación en el front, el número que se ve mientras se edita y el que
 * devuelve el PUT podrían no coincidir, y nadie sabría cuál creer.
 *
 * Editar un mes cambia el growth de **dos** filas: la propia y la siguiente, que
 * lo usa como base. Por eso el recálculo recorre la serie completa en orden y no
 * la fila tocada.
 */
import {
  growthMoMPct,
  netNewMrr,
  type RevenueMonth,
  type UpsertRevenueMonthInput,
} from '@ops/contracts'

import { leerNumero } from '../../lib/numeros'

/** Columnas que se editan. El resto de la fila son derivados o proveniencia. */
export const CAMPOS_EDITABLES = [
  'mrr',
  'clientesNuevos',
  'clientesChurned',
  'nuevoMrr',
  'expansionMrr',
  'churnedMrr',
] as const

export type CampoEditable = (typeof CAMPOS_EDITABLES)[number]

export const CAMPO_LABEL: Readonly<Record<CampoEditable, string>> = {
  mrr: 'MRR',
  clientesNuevos: 'Clientes nuevos',
  clientesChurned: 'Clientes churned',
  nuevoMrr: 'Nuevo MRR',
  expansionMrr: 'Expansión MRR',
  churnedMrr: 'Churn MRR',
}

/** Campos que se cuentan en unidades y no en dólares. */
export const CAMPOS_CONTEO: readonly CampoEditable[] = [
  'clientesNuevos',
  'clientesChurned',
]

/** Texto crudo tipeado por celda, indexado por período. Estado de la tabla. */
export type Borrador = Readonly<
  Record<string, Readonly<Partial<Record<CampoEditable, string>>>>
>

/** Ediciones ya parseadas y filtradas: solo lo que difiere del payload. */
export type Ediciones = Readonly<
  Record<string, Readonly<Partial<Record<CampoEditable, number | null>>>>
>

export function valorDe(
  mes: RevenueMonth,
  campo: CampoEditable,
): number | null {
  return mes[campo]
}

/**
 * Convierte el borrador de texto en ediciones numéricas, descartando lo que no
 * cambió y lo que no se puede leer como número.
 *
 * Una celda ilegible no se pisa el valor original: queda marcada como inválida
 * (ver `celdasInvalidas`) y bloquea el guardado.
 */
export function edicionesDe(
  meses: readonly RevenueMonth[],
  borrador: Borrador,
): Ediciones {
  const porPeriodo = new Map(meses.map((m) => [m.periodo, m]))
  const salida: Record<
    string,
    Partial<Record<CampoEditable, number | null>>
  > = {}

  for (const [periodo, campos] of Object.entries(borrador)) {
    const mes = porPeriodo.get(periodo)
    if (mes === undefined) continue

    for (const campo of CAMPOS_EDITABLES) {
      const texto = campos[campo]
      if (texto === undefined) continue

      const leido = leerNumero(texto)
      if (!leido.ok) continue
      if (leido.valor === valorDe(mes, campo)) continue

      const actual = salida[periodo] ?? {}
      actual[campo] = leido.valor
      salida[periodo] = actual
    }
  }

  return salida
}

/** Celdas con texto que no es un número. Bloquean guardar. */
export function celdasInvalidas(borrador: Borrador): string[] {
  const invalidas: string[] = []
  for (const [periodo, campos] of Object.entries(borrador)) {
    for (const campo of CAMPOS_EDITABLES) {
      const texto = campos[campo]
      if (texto === undefined) continue
      if (!leerNumero(texto).ok) invalidas.push(`${periodo}:${campo}`)
    }
  }
  return invalidas
}

export function periodosEditados(ediciones: Ediciones): string[] {
  return Object.entries(ediciones)
    .filter(([, campos]) => Object.keys(campos).length > 0)
    .map(([periodo]) => periodo)
    .sort()
}

/**
 * Aplica las ediciones y recalcula la serie completa: net new MRR, growth MoM y
 * la base del growth (`mrrBase` / `periodoBase`), que la regla de números
 * derivables obliga a publicar al lado del porcentaje.
 *
 * No muta la entrada.
 */
export function aplicarEdiciones(
  meses: readonly RevenueMonth[],
  ediciones: Ediciones,
): RevenueMonth[] {
  const conEdicion = meses.map((mes) => {
    const campos = ediciones[mes.periodo]
    if (campos === undefined) return { ...mes }
    const salida: RevenueMonth = { ...mes }
    for (const campo of CAMPOS_EDITABLES) {
      const valor = campos[campo]
      if (valor === undefined) continue
      salida[campo] = valor
    }
    return salida
  })

  return conEdicion.map((mes, i) => {
    const anterior = i === 0 ? undefined : conEdicion[i - 1]
    // El primer mes de la serie conserva la base que mandó la API: su mes previo
    // está fuera de la ventana y el panel no lo puede recalcular.
    const mrrBase = anterior === undefined ? mes.mrrBase : anterior.mrr
    const periodoBase =
      anterior === undefined ? mes.periodoBase : anterior.periodo

    const completo =
      mes.nuevoMrr !== null &&
      mes.expansionMrr !== null &&
      mes.churnedMrr !== null

    return {
      ...mes,
      mrrBase,
      periodoBase,
      netNewMrr: completo
        ? netNewMrr({
            nuevoMrr: mes.nuevoMrr as number,
            expansionMrr: mes.expansionMrr as number,
            churnedMrr: mes.churnedMrr as number,
          })
        : null,
      growthMoMPct:
        mes.mrr === null
          ? null
          : growthMoMPct({ actual: mes.mrr, anterior: mrrBase }),
    }
  })
}

/**
 * Los PUT que hay que mandar. Uno por mes tocado, con la fila completa: el
 * endpoint es un upsert del mes, no un PATCH de un campo.
 */
export function upsertsDe(
  mesesEditados: readonly RevenueMonth[],
  ediciones: Ediciones,
  nota?: string,
): UpsertRevenueMonthInput[] {
  const tocados = new Set(periodosEditados(ediciones))
  return mesesEditados
    .filter((mes) => tocados.has(mes.periodo))
    .map((mes) => ({
      periodo: mes.periodo,
      mrr: mes.mrr,
      clientesNuevos: mes.clientesNuevos,
      clientesChurned: mes.clientesChurned,
      nuevoMrr: mes.nuevoMrr,
      expansionMrr: mes.expansionMrr,
      churnedMrr: mes.churnedMrr,
      ...(nota === undefined ? {} : { nota }),
    }))
}

/** Texto de una celda: lo tipeado si hay borrador, si no el valor del payload. */
export function textoDeCelda(
  mes: RevenueMonth,
  campo: CampoEditable,
  borrador: Borrador,
  formatear: (valor: number | null) => string,
): string {
  const texto = borrador[mes.periodo]?.[campo]
  if (texto !== undefined) return texto
  return formatear(valorDe(mes, campo))
}
